// ABOUTME: Warehouse service desk label management page.
// ABOUTME: CRUD interface for support ticket labels — add at top, edit/delete per card.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { themeSettings, tokens } from "../../../../../../theme";
import {
    useGetSupportTicketLabelsQuery,
    useCreateSupportTicketLabelTypeMutation,
    useDeleteSupportTicketLabelTypeMutation,
} from "../../../../../../newapi/warehouse/supportTicketApiSlice";
import Subheading from "../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../components/theme/text/Text";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import { ShColorPicker } from "../../../../../../components/ShColorPicker";
import MConfirmDelete from "../../../../../../components/global/Modals/MConfirmDelete";
import MEditServiceDeskLabelModal from "../../../../../../components/global/Modals/MEditServiceDeskLabelModal";
import {
    showToastMessagePostRequest,
    showToastMessageUpdateRequest,
    showToastMessageRequestError,
} from "../../../../../../utils/toasts";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const DEFAULT_COLOR = "#6366F1";

const ServicedeskLabels = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: labelsData } = useGetSupportTicketLabelsQuery();
    const [createLabel, { isLoading: creating }] =
        useCreateSupportTicketLabelTypeMutation();
    const [deleteLabel] = useDeleteSupportTicketLabelTypeMutation();

    const [newShortName, setNewShortName] = useState("");
    const [newLongName, setNewLongName] = useState("");
    const [newColor, setNewColor] = useState({ hex: DEFAULT_COLOR, opacity: 1 });
    const [pickerAnchor, setPickerAnchor] = useState(null);

    const [editLabel, setEditLabel] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });
    }, []);

    const save = async () => {
        if (!newShortName || !newLongName) return;
        try {
            const result = await createLabel({
                shortName: newShortName,
                longName: newLongName,
                color: newColor.hex,
            }).unwrap();
            showToastMessagePostRequest(t, result);
            setNewShortName("");
            setNewLongName("");
            setNewColor({ hex: DEFAULT_COLOR, opacity: 1 });
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    const onEdit = (label) => {
        setEditLabel(label);
        setEditOpen(true);
    };

    const onDelete = (label) => {
        setDeleteTarget(label);
        setDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const result = await deleteLabel(deleteTarget.id).unwrap();
            showToastMessageUpdateRequest(t, result);
        } catch (err) {
            showToastMessageRequestError(t("delete_failed"), t, err);
        }
    };

    const labels = labelsData?.data ?? [];

    return (
        <Box
            sx={{
                height: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    overflow: "auto",
                    background:
                        theme.palette.mode === "dark"
                            ? colors.grey[300]
                            : colors.bg["tertiary"],
                    paddingX: 3,
                    paddingTop: 4,
                    paddingBottom: 4,
                    height: 1,
                    position: "relative",
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingTop: 2,
                        paddingX: 2,
                        background: colors.bg["tertiary"],
                    },
                }}
            >
                <Box
                    sx={{
                        mb:4,
                        background:
                            theme.palette.mode === "dark"
                                ? colors.grey[400]
                                : colors.grey[0],
                        border:
                            theme.palette.mode === "dark"
                                ? null
                                : `1px solid ${colors.grey[100]}`,
                        boxShadow:
                            theme.palette.mode === "dark"
                                ? null
                                : `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius: 3,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    <Subheading>{t("add_label")}</Subheading>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box
                            sx={{
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                borderRadius: 4,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                pl: 0.5,
                                pr: 1,
                                width: 140,
                            }}
                        >
                            <ButtonBase
                                onClick={(e) => setPickerAnchor(e.currentTarget)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 3,
                                    background: newColor.hex,
                                    flexShrink: 0,
                                    border: `1px solid ${colors.grey[200]}`,
                                }}
                            />
                            <InputBase
                                value={newColor.hex}
                                onChange={(e) =>
                                    setNewColor({ ...newColor, hex: e.target.value })
                                }
                                sx={{
                                    pl: 1,
                                    flex: 1,
                                    color: colors.txt["primary"],
                                    fontSize: 14,
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                borderRadius: 4,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                width: 160,
                            }}
                        >
                            <InputBase
                                placeholder={t("short_name")}
                                value={newShortName}
                                onChange={(e) => setNewShortName(e.target.value)}
                                sx={{ px: 2, flex: 1, color: colors.txt["primary"] }}
                            />
                        </Box>
                        <Box
                            sx={{
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                borderRadius: 4,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                minWidth: 200,
                            }}
                        >
                            <InputBase
                                placeholder={t("long_name")}
                                value={newLongName}
                                onChange={(e) => setNewLongName(e.target.value)}
                                sx={{ px: 2, flex: 1, color: colors.txt["primary"] }}
                            />
                        </Box>
                        <Shbutton
                            size={"32"}
                            isLoading={creating}
                            variant={"contained"}
                            onClick={save}
                            disabled={!newShortName || !newLongName}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        >
                            {t("save")}
                        </Shbutton>
                    </Box>
                </Box>

                <Subheading>
                    {t("labels")}
                </Subheading>
                <Box 
                    sx={{ 
                        mt:1,
                        display: "flex", 
                        flexDirection: "column", 
                        gap: 1 
                    }}
                >
                    {labels.map((label) => (
                        <Box
                            key={"label" + label.id}
                            sx={{
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[400]
                                        : colors.grey[0],
                                border:
                                    theme.palette.mode === "dark"
                                        ? null
                                        : `1px solid ${colors.grey[100]}`,
                                boxShadow:
                                    theme.palette.mode === "dark"
                                        ? null
                                        : `0px 2px 4px ${colors.grey[200]}`,
                                py: 1.5,
                                pl: 2,
                                pr: 1.5,
                                height:56,
                                borderRadius: 7,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    background: label.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection:'column',
                                    // gap: 1,
                                    flex: 1,
                                    minWidth: 0,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Text sx={{lineHeight:"16px"}} semibold>{label.shortName}</Text>
                                <Text sx={{lineHeight:"14px"}} light>{label.longName}</Text>
                            </Box>
                            <ButtonBase
                                onClick={() => onEdit(label)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    flexShrink: 0,
                                    borderRadius: 4,
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[300]
                                            : colors.grey[100],
                                    color: colors.txt["primary"],
                                }}
                            >
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={() => onDelete(label)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    flexShrink: 0,
                                    borderRadius: 4,
                                    background: themeSettings(theme.palette.mode).palette.red
                                        .main,
                                    color: themeSettings(theme.palette.mode).palette.red
                                        .contrastText,
                                }}
                            >
                                <DeleteOutlineOutlinedIcon />
                            </ButtonBase>
                        </Box>
                    ))}
                </Box>
            </Box>

            <ShColorPicker
                value={newColor}
                setValue={setNewColor}
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
            />

            <MEditServiceDeskLabelModal
                open={editOpen}
                label={editLabel}
                handleClose={() => setEditOpen(false)}
            />

            <MConfirmDelete
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                confirm={confirmDelete}
                data={deleteTarget?.shortName}
            />
        </Box>
    );
};

export default ServicedeskLabels;
